import React, { useState } from 'react';
import { Send, X, Mail, AlertCircle, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { filterProfanity } from '../utils/contentFilter';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Filter profanity from message
      const filteredMessage = filterProfanity(message);

      // Send email to admin
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          name: name,
          email: email,
          message: filteredMessage
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Contact form error:', errorData);
        throw new Error(errorData.error || 'Failed to send message');
      }

      setSuccess(true);
      setName('');
      setEmail('');
      setMessage('');

      // Close the form after showing success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Contact form submission failed:', err);
      setError(err.message || 'Couldn\'t send message — try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-primary-50 rounded-lg shadow-elegant w-full max-w-md border border-primary-200"
      >
        <div className="p-4 border-b border-primary-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-contrast flex items-center">
            <Mail size={20} className="mr-2 text-secondary" />
            Contact Support
          </h2>
          <button
            onClick={onClose}
            className="text-contrast-500 hover:text-contrast p-1 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-accent2" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-contrast">Message Sent!</h3>
              <p className="text-contrast-400">
                Thank you for contacting us. We'll get back to you as soon as possible.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 bg-secondary/10 p-3 rounded-md flex items-start">
                  <AlertCircle size={18} className="mr-2 mt-0.5 text-secondary flex-shrink-0" />
                  <p className="text-secondary">{error}</p>
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-contrast mb-1">
                  Your Name*
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-contrast mb-1">
                  Your Email*
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-contrast mb-1">
                  Message*
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                  placeholder="How can we help you?"
                  required
                ></textarea>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="mr-3 px-4 py-2 border border-primary-200 rounded-md hover:bg-primary-100 text-contrast"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-theme-primary text-white px-4 py-2 rounded-md hover:bg-theme-primary-darker transition-colors flex items-center disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ContactForm;